import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, type RegisterData } from '../services/authService';
import { logger } from '../utils/logger';
import {
  getAccessToken,
  setTokens,
  clearTokens,
  getRefreshToken,
  updateTokens,
  setRememberedEmail,
} from '../utils/authStorage';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  emailVerified: boolean;
  createdAt: string;
  isSuperuser: boolean;
  isStaff: boolean;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  register: (data: RegisterData) => Promise<{ user_id: string; message: string }>;
  logout: () => void;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = getAccessToken();
        if (token) {
          try {
            const userData = await authService.getCurrentUser();
            setUser(userData);
          } catch (error: any) {
            logger.debug('Error loading user data:', error);
            if (error?.message?.includes('401') || error?.message?.includes('Unauthorized')) {
              clearTokens();
            }
          }
        }
      } catch (error) {
        logger.debug('Auth initialization error:', error);
        clearTokens();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string, rememberMe = true) => {
    try {
      const response = await authService.login(email, password);
      setTokens(response.access, response.refresh, rememberMe);
      if (rememberMe) {
        setRememberedEmail(email);
      } else {
        setRememberedEmail(null);
      }
      const userData = await authService.getCurrentUser();
      setUser(userData);
    } catch (error) {
      throw error;
    }
  };

  const register = async (data: RegisterData): Promise<{ user_id: string; message: string }> => {
    try {
      logger.log('[AuthContext] Chamando authService.register...');
      const response = await authService.register(data);
      logger.log('[AuthContext] Resposta recebida do authService:', response);
      // Se chegou até aqui, o registro foi bem-sucedido
      // Garantir que a resposta tenha o formato esperado
      if (!response || (typeof response === 'object' && !response.user_id && !response.message)) {
        logger.warn('[AuthContext] Resposta inesperada, criando resposta padrão');
        return { user_id: '', message: 'Registro realizado com sucesso' };
      }
      return response;
    } catch (error) {
      logger.error('[AuthContext] Erro no register:', error);
      throw error;
    }
  };

  const logout = () => {
    clearTokens();
    setUser(null);
  };

  const refreshToken = async () => {
    try {
      const rt = getRefreshToken();
      if (!rt) throw new Error('No refresh token');
      const response = await authService.refreshToken(rt);
      updateTokens(response.access, response.refresh);
    } catch (error) {
      logout();
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    login,
    register,
    logout,
    refreshToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
